//src/pages/cadastros/RisksPage.tsx
import { useMemo, useState } from "react";
import { Eye, Pencil, Plus, Power, Search, Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import { EmptyState } from "@/components/shared/EmptyState";
import { PageHeader } from "@/components/shared/PageHeader";
import { PermissionIconButton } from "@/components/shared/PermissionIconButton";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  useDeleteRisk,
  useRiskCategories,
  useRisks,
  useSetRiskStatus,
} from "@/hooks/useRisks";
import { PERMISSIONS } from "@/lib/permissions";
import type { RiskCatalog, RiskTheme } from "@/types/risks";

const RISK_THEME_LABELS: Record<RiskTheme, string> = {
  psychosocial: "Psicossocial",
  physical: "Físico",
  chemical: "Químico",
  biological: "Biológico",
  ergonomic: "Ergonômico",
  accident: "Acidente",
  other: "Outro",
};

export default function RisksPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [riskTheme, setRiskTheme] = useState<RiskTheme | "">("");
  const [activeFilter, setActiveFilter] = useState<"all" | "active" | "inactive">(
    "all",
  );
  const [selectedItem, setSelectedItem] = useState<RiskCatalog | null>(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  const risksQ = useRisks({
    q: search,
    risk_category_id: categoryId || undefined,
    risk_theme: riskTheme || undefined,
    is_active: activeFilter === "all" ? undefined : activeFilter === "active",
  });
  const categoriesQ = useRiskCategories({
    risk_theme: riskTheme || undefined,
    is_active: true,
  });
  const setStatusMut = useSetRiskStatus();
  const deleteMut = useDeleteRisk();

  const items = useMemo(() => risksQ.data ?? [], [risksQ.data]);
  const categories = categoriesQ.data ?? [];
  const busy = setStatusMut.isPending || deleteMut.isPending;

  async function handleToggleActive(item: RiskCatalog) {
    try {
      await setStatusMut.mutateAsync({
        riskId: item.id,
        is_active: !item.is_active,
      });
      toast.success(
        item.is_active ? "Risco desativado com sucesso." : "Risco ativado com sucesso.",
      );
    } catch (e: any) {
      toast.error(e?.message || "Erro ao alterar status do risco.");
    }
  }

  async function handleDelete() {
    if (!selectedItem?.id) return;
    try {
      await deleteMut.mutateAsync(selectedItem.id);
      toast.success("Risco excluído com sucesso.");
      setConfirmDeleteOpen(false);
      setSelectedItem(null);
    } catch (e: any) {
      toast.error(e?.message || "Erro ao excluir risco.");
    }
  }

  return (
    <div>
      <PageHeader
        title="Riscos"
        description="Cadastre riscos e gerencie os planos de ação sugeridos para cada risco no próprio detalhe do cadastro."
      >
        <Button onClick={() => navigate("/cadastros/riscos/new")}> 
          <Plus className="mr-2 h-4 w-4" />
          Novo risco
        </Button>
      </PageHeader>

      <Card className="mb-4 p-4">
        <div className="grid grid-cols-1 gap-3 xl:grid-cols-[1.2fr_240px_240px_180px]">
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar por código, nome ou descrição..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8"
            />
          </div>

          <select
            className="h-10 rounded-md border bg-background px-3 text-sm"
            value={riskTheme}
            onChange={(e) => {
              setRiskTheme(e.target.value as RiskTheme | "");
              setCategoryId("");
            }}
          >
            <option value="">Todos os temas</option>
            {Object.entries(RISK_THEME_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>

          <select
            className="h-10 rounded-md border bg-background px-3 text-sm"
            value={categoryId}
            onChange={(e) => setCategoryId(e.target.value)}
          >
            <option value="">Todas as categorias</option>
            {categories.map((category) => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </select>

          <select
            className="h-10 rounded-md border bg-background px-3 text-sm"
            value={activeFilter}
            onChange={(e) =>
              setActiveFilter(e.target.value as "all" | "active" | "inactive")
            }
          >
            <option value="all">Todos os status</option>
            <option value="active">Ativos</option>
            <option value="inactive">Inativos</option>
          </select>
        </div>
      </Card>

      {risksQ.isLoading ? (
        <Card className="p-6 text-sm text-muted-foreground">Carregando riscos...</Card>
      ) : risksQ.isError ? (
        <Card className="p-6 text-sm text-destructive">
          {(risksQ.error as any)?.message || "Erro ao carregar riscos."}
        </Card>
      ) : items.length === 0 ? (
        <EmptyState
          title="Nenhum risco encontrado"
          description="Cadastre riscos e use o detalhe do risco para gerenciar os planos de ação vinculados."
        />
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Risco</TableHead>
                <TableHead>Categoria</TableHead>
                <TableHead>Tema</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-[180px] text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>
                    <div className="flex flex-col">
                      <span className="font-medium">{item.name}</span>
                      <span className="text-xs text-muted-foreground">{item.code}</span>
                    </div>
                  </TableCell>
                  <TableCell>{item.risk_category_name}</TableCell>
                  <TableCell>{RISK_THEME_LABELS[item.risk_theme]}</TableCell>
                  <TableCell>
                    <Badge variant={item.is_active ? "default" : "secondary"}>
                      {item.is_active ? "Ativo" : "Inativo"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <PermissionIconButton
                        label="Visualizar"
                        permission={PERMISSIONS.RISKS_READ}
                        onClick={() => navigate(`/cadastros/riscos/${item.id}`)}
                      >
                        <Eye className="h-4 w-4" />
                      </PermissionIconButton>

                      <PermissionIconButton
                        label="Editar"
                        permission={PERMISSIONS.RISKS_WRITE}
                        disabled={busy}
                        onClick={() => navigate(`/cadastros/riscos/${item.id}`)}
                      >
                        <Pencil className="h-4 w-4" />
                      </PermissionIconButton>

                      <PermissionIconButton
                        label={item.is_active ? "Desativar" : "Ativar"}
                        permission={PERMISSIONS.RISKS_WRITE}
                        disabled={busy}
                        destructive={item.is_active}
                        onClick={() => handleToggleActive(item)}
                      >
                        <Power className="h-4 w-4" />
                      </PermissionIconButton>

                      <PermissionIconButton
                        label="Excluir"
                        permission={PERMISSIONS.RISKS_WRITE}
                        disabled={busy}
                        destructive
                        onClick={() => {
                          setSelectedItem(item);
                          setConfirmDeleteOpen(true);
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </PermissionIconButton>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <ConfirmDialog
        open={confirmDeleteOpen}
        onOpenChange={setConfirmDeleteOpen}
        title="Excluir risco"
        description={`Tem certeza que deseja excluir o risco "${selectedItem?.name || ""}"?`}
        confirmText="Excluir"
        onConfirm={handleDelete}
        destructive
      />
    </div>
  );
}
